import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { adminApi, errorMessage } from "../../lib/api";
import { inputCls } from "../landing/Field";

const EMPTY = { name: "", email: "", password: "" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "Never");

const AddMember = ({ onAdded }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/admin/team", f);
      toast.success(`${f.name} added`);
      setF(EMPTY);
      onAdded();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const field = (k, props) => (
    <Input required data-testid={`team-add-${k}`} className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} />
  );
  return (
    <form onSubmit={submit} data-testid="team-add-form" className="grid grid-cols-1 gap-3 border border-white/10 bg-[#0B1120] p-4 sm:grid-cols-4">
      {field("name", { placeholder: "Full name" })}
      {field("email", { type: "email", placeholder: "Email" })}
      {field("password", { type: "text", placeholder: "Password (min 8)", autoComplete: "new-password" })}
      <button type="submit" disabled={busy} data-testid="team-add-submit"
        className="flex h-11 items-center justify-center gap-2 bg-orange-500 px-4 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
        <UserPlus className="h-4 w-4" />Add member
      </button>
    </form>
  );
};

const MemberRow = ({ m, onChanged }) => {
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const resetPw = () => {
    const password = window.prompt(`New password for ${m.email} (8+ characters, letters and numbers)`);
    if (password) act(() => adminApi.patch(`/admin/team/${m.id}`, { password }), "Password updated");
  };
  const remove = () => {
    if (window.confirm(`Remove ${m.email}? Their past KYC reviews stay recorded.`)) act(() => adminApi.delete(`/admin/team/${m.id}`), "Member removed");
  };
  return (
    <tr data-testid={`team-row-${m.email}`} className="border-t border-white/5 text-sm">
      <td className="py-3 pr-3"><p className="font-semibold text-white">{m.name}</p><p className="text-slate-400">{m.email}</p></td>
      <td className="py-3 pr-3">{m.is_owner ? <span className="text-orange-300">Owner</span> : "Team admin"}</td>
      <td className="py-3 pr-3 font-mono" data-testid={`team-reviews-${m.email}`}>{m.kyc_reviews}</td>
      <td className="py-3 pr-3 text-slate-400">{fmt(m.last_login_at)}</td>
      <td className="py-3">
        {!m.is_owner && (
          <div className="flex items-center gap-3">
            <Switch checked={m.active} data-testid={`team-active-${m.email}`}
              onCheckedChange={(v) => act(() => adminApi.patch(`/admin/team/${m.id}`, { active: v }), v ? "Account enabled" : "Account disabled")} />
            <button onClick={resetPw} data-testid={`team-reset-${m.email}`} title="Reset password" className="text-slate-400 hover:text-white"><KeyRound className="h-4 w-4" /></button>
            <button onClick={remove} data-testid={`team-delete-${m.email}`} title="Remove" className="text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </td>
    </tr>
  );
};

export const TeamDialog = ({ open, onClose }) => {
  const [members, setMembers] = useState([]);
  const load = useCallback(() => {
    adminApi.get("/admin/team").then((r) => setMembers(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="team-dialog" className="max-h-[90vh] max-w-4xl overflow-y-auto rounded-none border-white/10 bg-[#0F172A] text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-xl">Team admin accounts</DialogTitle></DialogHeader>
        <AddMember onAdded={load} />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-400">
              <tr><th className="pb-2">Member</th><th className="pb-2">Role</th><th className="pb-2">KYC reviews</th><th className="pb-2">Last login</th><th className="pb-2">Active / actions</th></tr>
            </thead>
            <tbody>{members.map((m) => <MemberRow key={m.id} m={m} onChanged={load} />)}</tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
